/**
 * 刀线 smoothRdp 公共绘制工具。
 *
 * 注意：本文件不处理 DPR。调用方必须保证 points、lineWidth 与当前 ctx 坐标系一致。
 */

export const DEFAULT_OUTLINE_RENDER_CONFIG = {
  // RDP 抽稀容差相对刀线宽度的倍率，值越大保留点越少、轮廓越平滑。
  toleranceLineWidthRatio: 0.8,
  // RDP 抽稀的最小容差，避免细刀线下容差过小导致像素台阶残留。
  minTolerance: 1.5,
  // 原始点回补为拐角点的最小转角角度，值越小越容易把转折点补回。
  cornerAngleThreshold: 65,
  // 拐角点回补要求的相邻段最小长度倍率，值越小拐角附近短段越容易保留。
  cornerMinDistanceLineWidthRatio: 2,
  // 平滑描边时判定硬角直连的角度阈值，值越小越容易保留尖锐拐角。
  smoothHardCornerAngle: 65,
  // 平滑描边时判定长线段直连的线宽倍率，值越小越容易减少长直线被贝塞尔拉弯。
  straightSegmentMinDistanceLineWidthRatio: 40,
  // 闭合轮廓是否先拆成两段开放弧再做 RDP，避免首尾相邻导致抽稀过保守。
  closedLoopRdp: true
};

/**
 * 获取完整渲染配置。
 * @param {Object} [config] - 调用方覆盖配置
 * @returns {Object} 合并后的渲染配置
 */
export function getOutlineRenderConfig(config) {
  return Object.assign({}, DEFAULT_OUTLINE_RENDER_CONFIG, config || {});
}

/**
 * 计算两点距离。
 * @param {Array<number>} point1 - 起点 [x, y]
 * @param {Array<number>} point2 - 终点 [x, y]
 * @returns {number}
 */
export function getPointDistance(point1, point2) {
  const dx = point2[0] - point1[0];
  const dy = point2[1] - point1[1];
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * 计算点到线段的最短距离。
 * @param {Array<number>} point - 待计算点 [x, y]
 * @param {Array<number>} startPoint - 线段起点 [x, y]
 * @param {Array<number>} endPoint - 线段终点 [x, y]
 * @returns {number}
 */
function getPointToSegmentDistance(point, startPoint, endPoint) {
  const segmentX = endPoint[0] - startPoint[0];
  const segmentY = endPoint[1] - startPoint[1];
  const segmentLengthSquare = segmentX * segmentX + segmentY * segmentY;

  // 线段退化成点时，直接计算到起点的距离。
  if (!segmentLengthSquare) {
    return getPointDistance(point, startPoint);
  }

  const pointX = point[0] - startPoint[0];
  const pointY = point[1] - startPoint[1];
  const ratio = (pointX * segmentX + pointY * segmentY) / segmentLengthSquare;
  const safeRatio = Math.max(0, Math.min(1, ratio));
  const projectedPoint = [
    startPoint[0] + safeRatio * segmentX,
    startPoint[1] + safeRatio * segmentY
  ];

  return getPointDistance(point, projectedPoint);
}

/**
 * 计算当前点前后两段的夹角变化。
 * @param {Array<number>} prevPoint - 前一个点 [x, y]
 * @param {Array<number>} currentPoint - 当前点 [x, y]
 * @param {Array<number>} nextPoint - 后一个点 [x, y]
 * @returns {number} 夹角变化角度
 */
function getAngleChange(prevPoint, currentPoint, nextPoint) {
  const prevX = currentPoint[0] - prevPoint[0];
  const prevY = currentPoint[1] - prevPoint[1];
  const nextX = nextPoint[0] - currentPoint[0];
  const nextY = nextPoint[1] - currentPoint[1];
  const prevLength = Math.sqrt(prevX * prevX + prevY * prevY);
  const nextLength = Math.sqrt(nextX * nextX + nextY * nextY);

  // 任一线段长度为 0 时无法形成有效夹角，按无转角处理。
  if (!prevLength || !nextLength) {
    return 0;
  }

  const dot = prevX * nextX + prevY * nextY;
  const cosine = Math.max(-1, Math.min(1, dot / (prevLength * nextLength)));

  return (Math.acos(cosine) * 180) / Math.PI;
}

/**
 * 使用 RDP 算法按整体误差抽稀点。
 * @param {Array<Array<number>>} points - 点集 [[x, y], ...]
 * @param {number} tolerance - 允许的最大轮廓误差
 * @returns {Array<Array<number>>}
 */
function simplifyPointsByRdp(points, tolerance) {
  // 点数不足以形成可抽稀线段时，直接返回副本。
  if (points.length < 3) {
    return points.slice();
  }

  const keepIndexes = new Set([0, points.length - 1]);
  const ranges = [[0, points.length - 1]];

  while (ranges.length) {
    const range = ranges.pop();
    const startIndex = range[0];
    const endIndex = range[1];
    let maxDistance = 0;
    let maxDistanceIndex = -1;

    for (let i = startIndex + 1; i < endIndex; i++) {
      const distance = getPointToSegmentDistance(
        points[i],
        points[startIndex],
        points[endIndex]
      );

      // 记录当前线段内偏离最大的点。
      if (distance > maxDistance) {
        maxDistance = distance;
        maxDistanceIndex = i;
      }
    }

    // 偏离超过容差时保留该点，并继续拆分两侧线段。
    if (maxDistance > tolerance && maxDistanceIndex > startIndex) {
      keepIndexes.add(maxDistanceIndex);
      ranges.push([startIndex, maxDistanceIndex], [maxDistanceIndex, endIndex]);
    }
  }

  return Array.from(keepIndexes)
    .sort(function (indexA, indexB) {
      return indexA - indexB;
    })
    .map(function (index) {
      return points[index];
    });
}

/**
 * 移除闭合轮廓末尾重复的首点。
 * @param {Array<Array<number>>} points - 闭合轮廓点集
 * @returns {Array<Array<number>>}
 */
function normalizeClosedOutlinePoints(points) {
  // 点数不足时没有重复闭合点可移除。
  if (points.length < 2) {
    return points.slice();
  }

  const firstPoint = points[0];
  const lastPoint = points[points.length - 1];

  // 首尾坐标一致时移除最后一个重复点，避免闭合 RDP 生成零长度段。
  if (firstPoint[0] === lastPoint[0] && firstPoint[1] === lastPoint[1]) {
    return points.slice(0, points.length - 1);
  }

  return points.slice();
}

/**
 * 获取闭合轮廓拆分 RDP 时使用的两个锚点下标。
 * @param {Array<Array<number>>} points - 闭合轮廓点集
 * @returns {{startIndex:number,endIndex:number}} 锚点下标
 */
function getClosedRdpAnchorIndexes(points) {
  let minXIndex = 0;
  let maxXIndex = 0;
  let minYIndex = 0;
  let maxYIndex = 0;

  points.forEach(function (point, index) {
    // 当前点更靠左时更新左侧锚点候选。
    if (point[0] < points[minXIndex][0]) {
      minXIndex = index;
    }

    // 当前点更靠右时更新右侧锚点候选。
    if (point[0] > points[maxXIndex][0]) {
      maxXIndex = index;
    }

    // 当前点更靠上时更新顶部锚点候选。
    if (point[1] < points[minYIndex][1]) {
      minYIndex = index;
    }

    // 当前点更靠下时更新底部锚点候选。
    if (point[1] > points[maxYIndex][1]) {
      maxYIndex = index;
    }
  });

  const horizontalDistance = getPointDistance(
    points[minXIndex],
    points[maxXIndex]
  );
  const verticalDistance = getPointDistance(
    points[minYIndex],
    points[maxYIndex]
  );

  // 使用跨度更大的方向拆分闭合环，减少首尾相邻导致的 RDP 保守抽点。
  if (horizontalDistance >= verticalDistance) {
    return {
      startIndex: minXIndex,
      endIndex: maxXIndex
    };
  }

  return {
    startIndex: minYIndex,
    endIndex: maxYIndex
  };
}

/**
 * 按闭合轮廓顺序截取两个下标之间的开放弧段。
 * @param {Array<Array<number>>} points - 闭合轮廓点集
 * @param {number} startIndex - 起始下标
 * @param {number} endIndex - 结束下标
 * @returns {Array<Array<number>>} 开放弧段点集
 */
function getClosedOutlineArcPoints(points, startIndex, endIndex) {
  const arcPoints = [points[startIndex]];
  let index = startIndex;

  while (index !== endIndex) {
    index = (index + 1) % points.length;
    arcPoints.push(points[index]);
  }

  return arcPoints;
}

/**
 * 对闭合轮廓执行 RDP 抽稀，避免把首尾相邻点当成开放路径端点。
 * @param {Array<Array<number>>} points - 闭合轮廓点集
 * @param {number} tolerance - 允许的最大轮廓误差
 * @returns {Array<Array<number>>} 抽稀后的闭合轮廓点集
 */
function simplifyClosedPointsByRdp(points, tolerance) {
  const normalizedPoints = normalizeClosedOutlinePoints(points);

  // 点数不足以形成闭合轮廓时回退普通 RDP。
  if (normalizedPoints.length < 4) {
    return simplifyPointsByRdp(normalizedPoints, tolerance);
  }

  const anchorIndexes = getClosedRdpAnchorIndexes(normalizedPoints);
  const firstArc = getClosedOutlineArcPoints(
    normalizedPoints,
    anchorIndexes.startIndex,
    anchorIndexes.endIndex
  );
  const secondArc = getClosedOutlineArcPoints(
    normalizedPoints,
    anchorIndexes.endIndex,
    anchorIndexes.startIndex
  );
  const simplifiedFirstArc = simplifyPointsByRdp(firstArc, tolerance);
  const simplifiedSecondArc = simplifyPointsByRdp(secondArc, tolerance);

  return simplifiedFirstArc
    .slice(0, simplifiedFirstArc.length - 1)
    .concat(simplifiedSecondArc.slice(0, simplifiedSecondArc.length - 1));
}

/**
 * 合并 RDP 抽稀点和明显转角点。
 * @param {Array<Array<number>>} points - 原始点集 [[x, y], ...]
 * @param {Array<Array<number>>} rdpPoints - RDP 抽稀后的点集
 * @param {number} angleThreshold - 需要保留的转角阈值
 * @param {number} minDistance - 转角相邻段最小长度
 * @param {boolean} closed - 是否闭合路径
 * @returns {Array<Array<number>>}
 */
function mergeRdpPointsWithCornerPoints(
  points,
  rdpPoints,
  angleThreshold,
  minDistance,
  closed
) {
  const keepPointSet = new Set(rdpPoints);
  const mergedPoints = [];

  // 点数不足以形成转角时，直接返回 RDP 结果。
  if (points.length < 3) {
    return rdpPoints.slice();
  }

  const startIndex = closed ? 0 : 1;
  const endIndex = closed ? points.length : points.length - 1;

  for (let i = startIndex; i < endIndex; i++) {
    const prevIndex = i === 0 ? points.length - 1 : i - 1;
    const nextIndex = i === points.length - 1 ? 0 : i + 1;
    const prevDistance = getPointDistance(points[prevIndex], points[i]);
    const nextDistance = getPointDistance(points[i], points[nextIndex]);
    const angle = getAngleChange(
      points[prevIndex],
      points[i],
      points[nextIndex]
    );

    // 相邻段太短时视为像素台阶，不额外合并回 RDP 点集。
    if (
      minDistance &&
      (prevDistance < minDistance || nextDistance < minDistance)
    ) {
      continue;
    }

    // 明显转角点即使不满足 RDP 容差也要保留，避免拐角被抹平。
    if (angle >= angleThreshold) {
      keepPointSet.add(points[i]);
    }
  }

  points.forEach(function (point) {
    // 只按原始顺序收集保留点，避免合并后路径顺序错乱。
    if (keepPointSet.has(point)) {
      mergedPoints.push(point);
    }
  });

  return mergedPoints;
}

/**
 * 使用 RDP 和转角保护抽稀轮廓点。
 * @param {Array<Array<number>>} points - 点集 [[x, y], ...]
 * @param {Object} options - 抽稀配置
 * @param {number} options.tolerance - 允许的最大轮廓误差
 * @param {number} options.angleThreshold - 需要保留的转角阈值
 * @param {number} options.cornerMinDistance - 转角相邻段最小长度
 * @param {boolean} options.closed - 是否闭合路径
 * @param {boolean} options.closedLoopRdp - 闭合路径是否启用拆弧 RDP
 * @returns {Array<Array<number>>}
 */
export function simplifyPointsByRdpWithCorners(points, options) {
  const rdpPoints =
    options.closed && options.closedLoopRdp
      ? simplifyClosedPointsByRdp(points, options.tolerance)
      : simplifyPointsByRdp(points, options.tolerance);

  return mergeRdpPointsWithCornerPoints(
    points,
    rdpPoints,
    options.angleThreshold,
    options.cornerMinDistance,
    options.closed
  );
}

/**
 * 将点集绘制为直线路径。
 * @param {CanvasRenderingContext2D} ctx - Canvas 上下文
 * @param {Array<Array<number>>} points - 点集 [[x, y], ...]
 * @param {boolean} closed - 是否闭合路径
 * @returns {void}
 */
function drawLinearOutlinePath(ctx, points, closed) {
  // 点数不足时不创建路径。
  if (points.length < 2) {
    return;
  }

  ctx.beginPath();
  ctx.moveTo(points[0][0], points[0][1]);

  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i][0], points[i][1]);
  }

  // 闭合轮廓保持 closePath 行为。
  if (closed) {
    ctx.closePath();
  }
}

/**
 * 计算两点中点。
 * @param {Array<number>} point1 - 第一个点 [x, y]
 * @param {Array<number>} point2 - 第二个点 [x, y]
 * @returns {Array<number>}
 */
function getMidPoint(point1, point2) {
  return [(point1[0] + point2[0]) / 2, (point1[1] + point2[1]) / 2];
}

/**
 * 计算点集密度统计。
 * @param {Array<Array<number>>} points - 点集 [[x, y], ...]
 * @returns {{count:number,averageDistance:number,minDistance:number,maxDistance:number,bounds:Object}}
 */
export function getPointDensityStats(points) {
  let bounds = {
    minX: 0,
    minY: 0,
    maxX: 0,
    maxY: 0,
    width: 0,
    height: 0
  };

  // 空点集没有可统计范围，返回零值。
  if (!points || !points.length) {
    return {
      count: 0,
      averageDistance: 0,
      minDistance: 0,
      maxDistance: 0,
      bounds: bounds
    };
  }

  let minX = points[0][0];
  let minY = points[0][1];
  let maxX = points[0][0];
  let maxY = points[0][1];
  let minDistance = Infinity;
  let maxDistance = 0;
  let totalDistance = 0;
  let distanceCount = 0;

  points.forEach(function (point, index) {
    minX = Math.min(minX, point[0]);
    minY = Math.min(minY, point[1]);
    maxX = Math.max(maxX, point[0]);
    maxY = Math.max(maxY, point[1]);

    // 从第二个点开始统计相邻点距离。
    if (index > 0) {
      const distance = getPointDistance(points[index - 1], point);

      minDistance = Math.min(minDistance, distance);
      maxDistance = Math.max(maxDistance, distance);
      totalDistance += distance;
      distanceCount++;
    }
  });

  bounds = {
    minX: minX,
    minY: minY,
    maxX: maxX,
    maxY: maxY,
    width: maxX - minX,
    height: maxY - minY
  };

  return {
    count: points.length,
    averageDistance: distanceCount ? totalDistance / distanceCount : 0,
    minDistance: distanceCount ? minDistance : 0,
    maxDistance: maxDistance,
    bounds: bounds
  };
}

/**
 * 获取 RDP 平滑绘制时判定硬角的最小段长。
 * @param {Array<Array<number>>} points - 点集 [[x, y], ...]
 * @param {Object} renderConfig - 轮廓渲染配置
 * @param {number} lineWidth - 刀线宽度
 * @returns {number}
 */
function getRdpSmoothHardCornerMinDistance(points, renderConfig, lineWidth) {
  const stats = getPointDensityStats(points);

  // 配置显式指定最小段长时优先使用配置，便于页面调试切换强弱。
  if (renderConfig.smoothHardCornerMinDistance) {
    return renderConfig.smoothHardCornerMinDistance;
  }

  return Math.max(
    Math.min(lineWidth * 2, stats.averageDistance * 0.8),
    lineWidth * 0.75,
    1
  );
}

/**
 * 判断 RDP 优化后的点是否为需要保留直线连接的硬角。
 * @param {Array<Array<number>>} points - 点集 [[x, y], ...]
 * @param {number} index - 当前点下标
 * @param {boolean} closed - 是否闭合路径
 * @param {number} minDistance - 判定硬角所需的最小相邻段长
 * @param {Object} renderConfig - 轮廓渲染配置
 * @returns {boolean}
 */
function isRdpSmoothHardCorner(
  points,
  index,
  closed,
  minDistance,
  renderConfig
) {
  const pointCount = points.length;
  const hardCornerAngle = renderConfig.smoothHardCornerAngle;

  // 开放路径首尾点没有完整前后段，不作为中间硬角处理。
  if (!closed && (index <= 0 || index >= pointCount - 1)) {
    return false;
  }

  const prevIndex = index === 0 ? pointCount - 1 : index - 1;
  const nextIndex = index === pointCount - 1 ? 0 : index + 1;
  const prevPoint = points[prevIndex];
  const currentPoint = points[index];
  const nextPoint = points[nextIndex];
  const prevDistance = getPointDistance(prevPoint, currentPoint);
  const nextDistance = getPointDistance(currentPoint, nextPoint);

  // 过短的像素级台阶不作为硬角，交给平滑曲线吸收。
  if (prevDistance < minDistance || nextDistance < minDistance) {
    return false;
  }

  return getAngleChange(prevPoint, currentPoint, nextPoint) >= hardCornerAngle;
}

/**
 * 获取 RDP 平滑绘制时需要强制直线连接的长线段阈值。
 * @param {Object} renderConfig - 轮廓渲染配置
 * @param {number} lineWidth - 刀线宽度
 * @returns {number}
 */
function getRdpStraightSegmentMinDistance(renderConfig, lineWidth) {
  // 配置显式指定长线段阈值时直接使用配置值。
  if (typeof renderConfig.straightSegmentMinDistance === 'number') {
    return renderConfig.straightSegmentMinDistance;
  }

  // 配置线宽倍率时按当前刀线宽度换算。
  if (
    typeof renderConfig.straightSegmentMinDistanceLineWidthRatio === 'number'
  ) {
    return lineWidth * renderConfig.straightSegmentMinDistanceLineWidthRatio;
  }

  return 0;
}

/**
 * 判断 RDP 优化后的点是否连接了需要保持直线的长线段。
 * @param {Array<Array<number>>} points - 点集 [[x, y], ...]
 * @param {number} index - 当前点下标
 * @param {boolean} closed - 是否闭合路径
 * @param {number} minDistance - 长线段判定阈值
 * @returns {boolean}
 */
function isRdpSmoothStraightSegmentPoint(points, index, closed, minDistance) {
  const pointCount = points.length;

  // 未配置长线段阈值时，不触发额外直线连接。
  if (!minDistance) {
    return false;
  }

  // 开放路径首尾点本身已经直连，不作为中间点额外处理。
  if (!closed && (index <= 0 || index >= pointCount - 1)) {
    return false;
  }

  const prevIndex = index === 0 ? pointCount - 1 : index - 1;
  const nextIndex = index === pointCount - 1 ? 0 : index + 1;
  const prevDistance = getPointDistance(points[prevIndex], points[index]);
  const nextDistance = getPointDistance(points[index], points[nextIndex]);

  return prevDistance >= minDistance || nextDistance >= minDistance;
}

/**
 * 统计 RDP 平滑路径中的硬角数量。
 * @param {Array<Array<number>>} points - 点集 [[x, y], ...]
 * @param {boolean} closed - 是否闭合路径
 * @param {Object} renderConfig - 轮廓渲染配置
 * @param {number} lineWidth - 刀线宽度
 * @returns {{count:number,minDistance:number}}
 */
export function getRdpSmoothHardCornerDebugInfo(
  points,
  closed,
  renderConfig,
  lineWidth
) {
  const minDistance = getRdpSmoothHardCornerMinDistance(
    points,
    renderConfig,
    lineWidth
  );
  let count = 0;

  points.forEach(function (_point, index) {
    // 满足硬角条件的点会在绘制时使用直线连接。
    if (
      isRdpSmoothHardCorner(points, index, closed, minDistance, renderConfig)
    ) {
      count++;
    }
  });

  return {
    count: count,
    minDistance: minDistance
  };
}

/**
 * 绘制 RDP 点集：普通段用二次贝塞尔平滑，硬角处直线保角。
 * @param {CanvasRenderingContext2D} ctx - Canvas 上下文
 * @param {Array<Array<number>>} points - 点集 [[x, y], ...]
 * @param {boolean} closed - 是否闭合路径
 * @param {Object} renderConfig - 轮廓渲染配置
 * @param {number} lineWidth - 刀线宽度
 * @returns {void}
 */
export function drawRdpSmoothOutlinePath(
  ctx,
  points,
  closed,
  renderConfig,
  lineWidth
) {
  const pointCount = points.length;

  // 点数不足时不创建路径。
  if (pointCount < 2) {
    return;
  }

  // 两个点只能直线连接。
  if (pointCount === 2) {
    drawLinearOutlinePath(ctx, points, closed);
    return;
  }

  const hardCornerMinDistance = getRdpSmoothHardCornerMinDistance(
    points,
    renderConfig,
    lineWidth
  );
  const hardCornerMap = points.map(function (_point, index) {
    return isRdpSmoothHardCorner(
      points,
      index,
      closed,
      hardCornerMinDistance,
      renderConfig
    );
  });
  const straightSegmentMinDistance = getRdpStraightSegmentMinDistance(
    renderConfig,
    lineWidth
  );
  const straightSegmentMap = points.map(function (_point, index) {
    return isRdpSmoothStraightSegmentPoint(
      points,
      index,
      closed,
      straightSegmentMinDistance
    );
  });

  ctx.beginPath();

  // 闭合路径从首尾中点开始，确保首尾衔接也参与平滑。
  if (closed) {
    const startMid = getMidPoint(points[pointCount - 1], points[0]);
    ctx.moveTo(startMid[0], startMid[1]);

    for (let i = 0; i < pointCount; i++) {
      const currentPoint = points[i];
      const nextPoint = points[(i + 1) % pointCount];
      const endMid = getMidPoint(currentPoint, nextPoint);

      // 当前点为硬角或连接长线段时，先直线进入该点，再直线离开。
      if (hardCornerMap[i] || straightSegmentMap[i]) {
        ctx.lineTo(currentPoint[0], currentPoint[1]);
        ctx.lineTo(endMid[0], endMid[1]);
      } else {
        ctx.quadraticCurveTo(
          currentPoint[0],
          currentPoint[1],
          endMid[0],
          endMid[1]
        );
      }
    }

    ctx.closePath();
    return;
  }

  ctx.moveTo(points[0][0], points[0][1]);

  for (let j = 1; j < pointCount - 1; j++) {
    const controlPoint = points[j];
    const nextControlPoint = points[j + 1];
    const endPoint = getMidPoint(controlPoint, nextControlPoint);

    // 当前点为硬角或连接长线段时，直线穿过该点，避免贝塞尔拉圆。
    if (hardCornerMap[j] || straightSegmentMap[j]) {
      ctx.lineTo(controlPoint[0], controlPoint[1]);
      ctx.lineTo(endPoint[0], endPoint[1]);
    } else {
      ctx.quadraticCurveTo(
        controlPoint[0],
        controlPoint[1],
        endPoint[0],
        endPoint[1]
      );
    }
  }

  ctx.lineTo(points[pointCount - 1][0], points[pointCount - 1][1]);
}

/**
 * 获取 RDP 绘制容差。
 * @param {Object} renderConfig - 轮廓渲染配置
 * @param {number} lineWidth - 刀线宽度
 * @returns {number}
 */
export function getRdpTolerance(renderConfig, lineWidth) {
  let ratio = renderConfig.toleranceLineWidthRatio;
  let minTolerance = renderConfig.minTolerance;

  // 未配置比例时保持原有 RDP 容差。
  if (typeof ratio !== 'number') {
    ratio = 0.5;
  }

  // 未配置最小容差时保持原有 RDP 下限。
  if (typeof minTolerance !== 'number') {
    minTolerance = 1;
  }

  return Math.max(lineWidth * ratio, minTolerance);
}

/**
 * 获取 RDP 转角合并时的最小相邻段长。
 * @param {Object} renderConfig - 轮廓渲染配置
 * @param {number} lineWidth - 刀线宽度
 * @returns {number} 最小相邻段长
 */
export function getRdpCornerMinDistance(renderConfig, lineWidth) {
  // 显式配置最小段长时直接使用配置值。
  if (typeof renderConfig.cornerMinDistance === 'number') {
    return renderConfig.cornerMinDistance;
  }

  // 配置线宽倍率时按当前刀线宽度换算。
  if (typeof renderConfig.cornerMinDistanceLineWidthRatio === 'number') {
    return lineWidth * renderConfig.cornerMinDistanceLineWidthRatio;
  }

  return 0;
}

/**
 * 使用 smoothRdp 绘制单段刀线路径。
 * @param {CanvasRenderingContext2D} ctx - Canvas 上下文
 * @param {Array<Array<number>>} points - 点集 [[x, y], ...]
 * @param {boolean} closed - 是否闭合路径
 * @param {number} lineWidth - 刀线宽度
 * @param {Object} [renderConfig] - 轮廓渲染配置
 * @returns {Array<Array<number>>} 实际绘制使用的优化后点集
 */
export function drawSmoothOutlinePath(
  ctx,
  points,
  closed,
  lineWidth,
  renderConfig
) {
  const currentRenderConfig = getOutlineRenderConfig(renderConfig);

  // 点数不足时没有可绘制线段。
  if (!points || points.length < 2) {
    return [];
  }

  const simplifiedPoints = simplifyPointsByRdpWithCorners(points, {
    tolerance: getRdpTolerance(currentRenderConfig, lineWidth),
    angleThreshold: currentRenderConfig.cornerAngleThreshold,
    cornerMinDistance: getRdpCornerMinDistance(currentRenderConfig, lineWidth),
    closed: closed,
    closedLoopRdp: currentRenderConfig.closedLoopRdp
  });

  // 抽稀后点数不足时，不创建空路径。
  if (simplifiedPoints.length < 2) {
    return simplifiedPoints;
  }

  drawRdpSmoothOutlinePath(
    ctx,
    simplifiedPoints,
    closed,
    currentRenderConfig,
    lineWidth
  );
  ctx.stroke();

  return simplifiedPoints;
}
